// ═══════════════════════════════════════════════════════════════
// StudioImages — ITRIIA Studio onglet Images (S52 Phase 3)
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import {
    Sparkles, Loader2, Image as ImageIcon, Download, Trash2, FolderInput, X,
    RefreshCw, RotateCcw, AlertTriangle, Wand2, Undo2, ChevronDown, ChevronUp, HelpCircle,
    Mic, MicOff, Clock,
} from 'lucide-react';
import {
    getStudioImageModels, getHistory, deleteGeneration,
    exportToProject, getProjects, generateImage,
} from '../../services/studioImagesApi';
import { translate as dialecteTranslate, DialecteError } from '../../services/dialecteApi';
import { signUrl } from '../../services/signedUrlApi';
import useSignedSrc from '../../hooks/useSignedSrc';
import useAudioRecorder from '../../hooks/useAudioRecorder';
import { toast, confirm } from '../ui/Toast';

const DIALECTE_ONBOARDED_KEY = 'dialecte.onboarded';

const fmtCost = (cost) => {
    if (!cost || cost <= 0) return 'coût inconnu';
    if (cost < 0.01) return `$${cost.toFixed(5)}`;
    return `$${cost.toFixed(4)}`;
};

const phaseLabel = (phase) => ({
    validate: 'Validation du modèle…',
    refs: 'Préparation des références…',
    queued: 'Envoi provider…',
    provider_call: 'Génération en cours…',
    persist: 'Stockage de l\'image…',
}[phase] || 'En cours…');

const suggestionFor = (code) => ({
    provider_unavailable: 'Provider indisponible. Réessaie dans quelques secondes.',
    quota_exceeded: 'Quota dépassé. Attends ou change de modèle.',
    rate_limited: 'Trop de générations. Attends une minute avant de réessayer (limite : 20/min).',
    invalid_params: 'Paramètres invalides. Vérifie le prompt ou le modèle.',
    timeout: 'Temps dépassé. Réessaie avec un prompt plus simple.',
    content_policy: 'Contenu rejeté par le filtre de modération. Reformule.',
    provider_derivation: 'Le modèle n\'a pas retourné d\'image. Essaie un autre modèle (Nano Banana 2).',
    aborted: 'Annulée.',
    network: 'Erreur réseau.',
    feature_disabled: 'Studio Images désactivé par l\'administrateur.',
}[code] || 'Réessaie ou change de modèle.');

const StudioImages = () => {
    const [models, setModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState('');
    const [prompt, setPrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const [phase, setPhase] = useState(null);
    const [elapsed, setElapsed] = useState(0);
    const [result, setResult] = useState(null);       // { id, primary_asset_url, cost_usd, model, prompt }
    const [errorResult, setErrorResult] = useState(null); // { code, message, params }
    const [history, setHistory] = useState([]);
    const [projects, setProjects] = useState([]);
    const [exportModal, setExportModal] = useState(null); // { generationId }
    const [lightbox, setLightbox] = useState(null);       // url signée plein écran

    // Signed URL pour l'image principale (re-signée quand result change)
    const signedResultSrc = useSignedSrc(result?.primary_asset_url || null);

    // Ouvre le lightbox après avoir signé l'URL (évite fuite JWT dans src)
    const openLightbox = (assetPath) => {
        if (!assetPath) return;
        signUrl(assetPath).then(setLightbox).catch(() => toast.error('Lien expiré, réessaye.'));
    };

    // ─── Dialecte (S54 P1) ──────────────────────────────────────
    const [rawPromptBackup, setRawPromptBackup] = useState(null);
    const [optimizedData, setOptimizedData] = useState(null); // { rationale, cost_usd, model_used, deep } | null
    const [translating, setTranslating] = useState(false);
    const [deepMode, setDeepMode] = useState(false);
    // ─── S55 P3 : V2 exploration ─────────────────────────────────
    const [exploreMode, setExploreMode] = useState(false);
    const [variations, setVariations] = useState([]); // [{rationale, cost_usd, model_used, deep, optimized_prompt}, x3]
    const [rationaleOpen, setRationaleOpen] = useState(false);
    const [onboarding, setOnboarding] = useState(null); // null | { modelLabel }

    // ─── Vocal (S54 P2) ─────────────────────────────────────────
    const { isRecording, seconds: recSeconds, audioBlob, error: recError, startRecording, stopRecording, resetRecording, formatTime } = useAudioRecorder();
    const [transcribing, setTranscribing] = useState(false);

    // ─── Feature flags (S54 P3) ─────────────────────────────────
    const [featuresFlags, setFeaturesFlags] = useState({ dialecte: true }); // optimiste, override au fetch
    const containerRef = useRef(null);

    const abortRef = useRef(null);
    const timerRef = useRef(null);

    // ─── Chargements initiaux ───────────────────────────────────
    useEffect(() => {
        getStudioImageModels()
            .then(m => {
                setModels(m);
                if (m.length > 0) setSelectedModel(m[0].model_id);
            })
            .catch(e => toast.error(`Chargement modèles: ${e.message}`));
        refreshHistory();
        getProjects().then(p => setProjects(p)).catch(() => {});

        // Feature flags (S54 P3)
        const token = localStorage.getItem('oracle_token');
        fetch('/api/capabilities/features', { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d?.features) setFeaturesFlags(prev => ({ ...prev, ...d.features })); })
            .catch(() => { /* silencieux, on garde les defaults optimistes */ });
    }, []);

    const refreshHistory = () => {
        getHistory({ limit: 10 })
            .then(d => setHistory(d.items || []))
            .catch(() => {});
    };

    // ─── Timer écoulé pendant génération ────────────────────────
    useEffect(() => {
        if (loading) {
            const t0 = Date.now();
            timerRef.current = setInterval(() => {
                setElapsed(Math.floor((Date.now() - t0) / 1000));
            }, 500);
        } else {
            if (timerRef.current) clearInterval(timerRef.current);
            setElapsed(0);
        }
        return () => timerRef.current && clearInterval(timerRef.current);
    }, [loading]);

    // ─── Clear Dialecte optimisation si modèle change ───────────
    useEffect(() => {
        if (optimizedData !== null) {
            setOptimizedData(null);
            setRationaleOpen(false);
            toast.info('Prompt traduit pour l\'ancien modèle, re-traduis si besoin');
        }
    }, [selectedModel]); // eslint-disable-line

    // ─── Handlers Dialecte (S54 P1) ─────────────────────────────
    const mapDialecteError = (err) => {
        if (err instanceof DialecteError) {
            if (err.status === 429) {
                const sec = err.retryAfter ? `${err.retryAfter}s` : 'une minute';
                return `Trop de traductions, réessaie dans ${sec}`;
            }
            if (err.status === 503) return 'Le Dialecte est indisponible, tu peux générer sur ton brouillon';
            if (err.status === 400) return err.message || 'Prompt invalide (min 3 caractères)';
            return err.message || `Erreur Dialecte (${err.status})`;
        }
        if (err && err.name === 'AbortError') return 'Traduction trop longue, brouillon conservé';
        return 'Pas de réseau, brouillon conservé';
    };

    const performTranslate = async () => {
        const raw = prompt.trim();
        const modelId = selectedModel;
        if (!raw || raw.length < 3 || !modelId || translating) return;

        setRawPromptBackup(prompt);
        setTranslating(true);

        // ─── S55 P3 : Mode exploration (3 variantes en parallèle) ───
        if (exploreMode) {
            try {
                const results = await Promise.allSettled([
                    dialecteTranslate(modelId, raw, { deep: deepMode }),
                    dialecteTranslate(modelId, raw, { deep: deepMode }),
                    dialecteTranslate(modelId, raw, { deep: deepMode }),
                ]);
                const variants = results
                    .filter(r => r.status === 'fulfilled' && r.value?.optimized_prompt)
                    .map(r => ({
                        optimized_prompt: r.value.optimized_prompt,
                        rationale: r.value.rationale || '',
                        cost_usd: typeof r.value.cost_usd === 'number' ? r.value.cost_usd : 0,
                        model_used: r.value.model_used || 'modèle inconnu',
                        deep: !!r.value.deep,
                    }));
                if (variants.length === 0) {
                    // Toutes ont échoué : afficher l'erreur de la 1ère
                    const firstErr = results.find(r => r.status === 'rejected');
                    toast.error(firstErr ? mapDialecteError(firstErr.reason) : 'Aucune variante produite');
                } else {
                    const totalCost = variants.reduce((s, v) => s + v.cost_usd, 0);
                    setVariations(variants);
                    setOptimizedData(null); // reset la bulle simple si c'était ouvert
                    setRationaleOpen(false);
                    if (variants.length < 3) {
                        toast.warning(`${variants.length}/3 variantes reçues — ${fmtCost(totalCost)}`);
                    } else {
                        toast.success(`3 variantes — ${fmtCost(totalCost)}`);
                    }
                }
            } finally {
                setTranslating(false);
            }
            return;
        }

        // ─── Mode standard (1 variante) ────────────────────────────
        try {
            const body = await dialecteTranslate(modelId, raw, { deep: deepMode });
            if (body.optimized_prompt && typeof body.optimized_prompt === 'string') {
                setPrompt(body.optimized_prompt);
                setOptimizedData({
                    rationale: body.rationale || '',
                    cost_usd: typeof body.cost_usd === 'number' ? body.cost_usd : 0,
                    model_used: body.model_used || 'modèle inconnu',
                    deep: !!body.deep,
                    bench_id: body.bench_id || null, // S55 P4 : permet le link asset → bench
                });
                setRationaleOpen(false);
                toast.success(`Traduit — ${fmtCost(body.cost_usd)}`);
            } else {
                toast.error('Réponse Dialecte invalide, brouillon conservé');
            }
        } catch (err) {
            toast.error(mapDialecteError(err));
        } finally {
            setTranslating(false);
        }
    };

    const applyVariation = (variation) => {
        setPrompt(variation.optimized_prompt);
        setOptimizedData({
            rationale: variation.rationale,
            cost_usd: variation.cost_usd,
            model_used: variation.model_used,
            deep: variation.deep,
        });
        setVariations([]);
        setRationaleOpen(false);
        toast.success('Variante appliquée');
    };

    const clearVariations = () => {
        setVariations([]);
    };

    const handleTranslate = () => {
        const raw = prompt.trim();
        if (!raw || raw.length < 3) return toast.warning('Prompt trop court (min 3 caractères)');
        if (!selectedModel) return toast.error('Sélectionne un modèle');
        if (translating) return;

        const onboarded = localStorage.getItem(DIALECTE_ONBOARDED_KEY) === 'true';
        if (!onboarded) {
            const meta = models.find(m => m.model_id === selectedModel);
            setOnboarding({ modelLabel: meta?.label || 'ce modèle' });
            return;
        }
        performTranslate();
    };

    const handleOnboardAccept = () => {
        localStorage.setItem(DIALECTE_ONBOARDED_KEY, 'true');
        setOnboarding(null);
        performTranslate();
    };

    const handleOnboardDismiss = () => {
        // Pas de flag → seconde chance au prochain clic Traduire
        setOnboarding(null);
    };

    const handleRevert = () => {
        if (rawPromptBackup === null) return;
        setPrompt(rawPromptBackup);
        setOptimizedData(null);
        setRationaleOpen(false);
        setRawPromptBackup(null);
        toast.success('Brouillon restauré');
    };

    // ─── Handlers Vocal (S54 P2) ────────────────────────────────
    const handleMicClick = async () => {
        if (transcribing || translating || loading) return;
        if (!isRecording) {
            await startRecording();
            return;
        }
        // stop: cas < 1s ignoré dans useEffect([audioBlob])
        stopRecording();
    };

    // Auto-stop à 60s (garde-fou)
    useEffect(() => {
        if (isRecording && recSeconds >= 60) {
            stopRecording();
            toast.info('Enregistrement limité à 60s — transcription en cours');
        }
    }, [isRecording, recSeconds, stopRecording]);

    // Envoi du blob à Whisper dès disponibilité
    useEffect(() => {
        if (!audioBlob) return;
        // Si trop court (clic accidentel), ignore silencieusement
        if (recSeconds < 1) {
            resetRecording();
            return;
        }

        const sendTranscription = async () => {
            setTranscribing(true);
            try {
                const form = new FormData();
                form.append('file', audioBlob, 'recording.webm');
                form.append('language', 'fr');

                const token = localStorage.getItem('oracle_token');
                const res = await fetch('/api/voice/transcribe', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: form,
                });
                const body = await res.json().catch(() => ({}));
                if (!res.ok) {
                    toast.error(body.details || body.error || `Erreur transcription (${res.status})`);
                    return;
                }
                const transcript = (body.text || '').trim();
                if (!transcript) {
                    toast.warning('Aucun texte détecté');
                    return;
                }
                setPrompt(prev => prev + (prev ? '\n' : '') + transcript);
                toast.success('Transcription ajoutée');
            } catch (err) {
                toast.error('Transcription échouée : ' + (err.message || 'réseau'));
            } finally {
                setTranscribing(false);
                resetRecording();
            }
        };

        sendTranscription();
        // eslint-disable-next-line
    }, [audioBlob]);

    // Toast permission micro refusée
    useEffect(() => {
        if (recError) toast.error(recError);
    }, [recError]);

    // ─── Raccourci Ctrl+Shift+T scopé (S54 P3) ──────────────────
    useEffect(() => {
        if (!featuresFlags.dialecte) return undefined;
        const handler = (e) => {
            if (e.key !== 'T' && e.key !== 't') return;
            if (!(e.ctrlKey || e.metaKey) || !e.shiftKey) return;
            const container = containerRef.current;
            if (!container || !container.contains(document.activeElement)) return;
            if (translating || loading) return;
            if (prompt.trim().length < 3) return;
            e.preventDefault();
            e.stopPropagation();
            handleTranslate();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
        // eslint-disable-next-line
    }, [featuresFlags.dialecte, prompt, translating, loading, selectedModel]);

    // ─── Handlers ───────────────────────────────────────────────
    const handleGenerate = async (overrides = {}) => {
        const finalPrompt = (overrides.prompt || prompt).trim();
        const finalModel = overrides.model || selectedModel;
        const finalSeed = overrides.seed != null ? overrides.seed : null;

        if (!finalPrompt) return toast.warning('Entrez une description');
        if (finalPrompt.length > 2000) return toast.error('Prompt trop long (max 2000 chars)');
        if (!finalModel) return toast.error('Sélectionnez un modèle');

        // Confirmation si modèle tier premium/oracle (coût potentiellement élevé)
        const modelMeta = models.find(m => m.model_id === finalModel);
        if (!overrides._skipConfirm && modelMeta && (modelMeta.tier === 'premium' || modelMeta.tier === 'oracle')) {
            const ok = await confirm(
                `${modelMeta.label} est un modèle ${modelMeta.tier}. Le coût par image peut être élevé ($0.10+). Continuer ?`,
                { danger: false }
            );
            if (!ok) return;
        }

        setLoading(true);
        setPhase('validate');
        setResult(null);
        setErrorResult(null);

        const params = { model: finalModel, prompt: finalPrompt };
        if (finalSeed != null) params.seed = finalSeed;
        // S54 P4 : tracking Dialecte (backend-trusted, booléen seul)
        // S55 P4 : + bench_id pour lier l'asset généré à la row BENCH
        if (optimizedData) {
            params.options = { dialecte_used: true };
            if (optimizedData.bench_id) params.options.dialecte_bench_id = optimizedData.bench_id;
        }

        abortRef.current = generateImage(params, {
            onStatus: (s) => setPhase(s.phase),
            onImage: () => {},
            onDone: (d) => {
                setResult({
                    id: d.id,
                    primary_asset_url: d.primary_asset_url,
                    cost_usd: d.cost_usd,
                    model: d.model,
                    model_id: finalModel,
                    prompt: finalPrompt,
                    seed: finalSeed,
                });
                setLoading(false);
                setPhase(null);
                toast.success(`Image générée — ${fmtCost(d.cost_usd)}`);
                refreshHistory();
            },
            onError: (err) => {
                setErrorResult({ ...err, params });
                setLoading(false);
                setPhase(null);
                toast.error(`${err.code || 'error'}: ${err.message || 'erreur inconnue'}`);
            },
        });
    };

    const handleAbort = () => {
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }
    };

    const handleRetry = () => {
        if (!errorResult?.params) return;
        setErrorResult(null);
        handleGenerate({ _skipConfirm: true });
    };

    const handleRegenerate = () => {
        if (!result) return;
        const nextSeed = (result.seed ?? Date.now()) + 1;
        handleGenerate({
            model: result.model_id,
            prompt: result.prompt,
            seed: nextSeed,
            _skipConfirm: true,
        });
    };

    const handleDelete = async (id) => {
        const ok = await confirm('Supprimer cette génération de l\'historique ?', { danger: true });
        if (!ok) return;
        try {
            await deleteGeneration(id);
            toast.success('Supprimée');
            refreshHistory();
            if (result?.id === id) setResult(null);
        } catch (e) {
            toast.error(e.message);
        }
    };

    const handleExport = async (projectId) => {
        if (!exportModal) return;
        try {
            const out = await exportToProject(exportModal.generationId, projectId);
            toast.success(`Exportée vers le projet`);
            setExportModal(null);
            refreshHistory();
        } catch (e) {
            toast.error(e.code === 'already_exported' ? 'Déjà exportée' : e.message);
        }
    };

    const handleDownload = (url) => {
        const token = localStorage.getItem('oracle_token');
        fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.blob())
            .then(blob => {
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `studio-${Date.now()}.png`;
                link.click();
                URL.revokeObjectURL(link.href);
            })
            .catch(() => toast.error('Téléchargement échoué'));
    };

    // ─── Render ─────────────────────────────────────────────────
    const modelMeta = models.find(m => m.model_id === selectedModel);

    return (
        <div ref={containerRef} style={{ display: 'flex', gap: '24px', height: '100%', padding: '24px 0' }}>

            {/* Colonne gauche — Configuration ────────────────── */}
            <div style={{ flex: '0 0 380px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

                <Field label="Modèle">
                    <select
                        value={selectedModel}
                        onChange={e => setSelectedModel(e.target.value)}
                        style={selectStyle}
                        disabled={loading}
                    >
                        {models.map(m => (
                            <option key={m.model_id} value={m.model_id}>
                                {m.label} — {m.provider} ({m.tier})
                            </option>
                        ))}
                    </select>
                    {modelMeta?.one_liner && (
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                            {modelMeta.one_liner}
                        </div>
                    )}
                    {/* S55 P2 : bandeau thinking image models (GPT-5 Image, Gemini 3 Pro Image) */}
                    {modelMeta?.metadata?.is_thinking_image && (
                        <div
                            style={{
                                marginTop: '8px',
                                padding: '8px 12px',
                                background: 'rgba(245, 158, 11, 0.08)',
                                border: '1px solid rgba(245, 158, 11, 0.3)',
                                borderRadius: '8px',
                                fontSize: '12px',
                                color: 'var(--text-secondary)',
                                display: 'flex', alignItems: 'center', gap: '8px',
                            }}
                            title="Ce modèle fait du reasoning avant de produire l'image — comptez 30-60s."
                        >
                            <Clock size={13} style={{ color: '#f59e0b', flexShrink: 0 }} />
                            <span>
                                <strong style={{ color: 'var(--text)' }}>Modèle approfondi</strong> · génération 30-60s
                            </span>
                        </div>
                    )}
                </Field>

                <Field label={`Description (${prompt.length}/2000)`}>
                    <textarea
                        value={prompt}
                        onChange={e => setPrompt(e.target.value)}
                        placeholder="Décris l'image que tu veux générer…"
                        rows={6}
                        maxLength={2000}
                        disabled={loading}
                        style={{ ...inputBaseStyle, resize: 'vertical', fontFamily: 'inherit' }}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleGenerate(); }
                        }}
                    />
                </Field>

                {/* ─── Dialecte : contrôles (S54 P1) + Vocal (P2) ─ */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* Ligne micro + toggle + bouton Traduire */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <button
                            onClick={handleMicClick}
                            disabled={transcribing || translating || loading || !!recError}
                            style={{
                                ...btnGhost,
                                padding: '6px 10px', fontSize: '12px',
                                borderColor: isRecording ? '#dc2626' : 'var(--card-border)',
                                color: isRecording ? '#dc2626' : 'var(--text)',
                                animation: isRecording ? 'micpulse 1.2s ease-in-out infinite' : undefined,
                                cursor: (transcribing || translating || loading || !!recError) ? 'not-allowed' : 'pointer',
                                opacity: (transcribing || translating || loading || !!recError) ? 0.6 : 1,
                            }}
                            title={
                                recError ? 'Microphone indisponible'
                                    : isRecording ? 'Cliquer pour arrêter'
                                        : 'Dicter le prompt'
                            }
                        >
                            {transcribing ? (
                                <>
                                    <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                                    Transcription…
                                </>
                            ) : isRecording ? (
                                <>
                                    <Mic size={14} />
                                    REC {formatTime(recSeconds)}
                                </>
                            ) : recError ? (
                                <>
                                    <MicOff size={14} />
                                    Micro
                                </>
                            ) : (
                                <>
                                    <Mic size={14} />
                                    Micro
                                </>
                            )}
                        </button>
                        {featuresFlags.dialecte && (
                            <>
                                <label
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                                        fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer',
                                        userSelect: 'none',
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={deepMode}
                                        onChange={e => setDeepMode(e.target.checked)}
                                        disabled={translating || loading}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    Mode approfondi
                                    <span
                                        title="Analyse plus fine (~$0.005, Sonnet) vs rapide (<$0.001, Haiku)"
                                        style={{ display: 'inline-flex', color: 'var(--text-secondary)', opacity: 0.7 }}
                                    >
                                        <HelpCircle size={13} />
                                    </span>
                                </label>
                                {/* S55 P3 : toggle Mode exploration (3 variantes parallèles) */}
                                <label
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                                        fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer',
                                        userSelect: 'none',
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={exploreMode}
                                        onChange={e => setExploreMode(e.target.checked)}
                                        disabled={translating || loading}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    Mode exploration
                                    <span
                                        title="3 variantes Dialecte pour choisir (coût ~3x). Clique sur celle qui te plaît."
                                        style={{ display: 'inline-flex', color: 'var(--text-secondary)', opacity: 0.7 }}
                                    >
                                        <HelpCircle size={13} />
                                    </span>
                                </label>
                                <div style={{ flex: 1 }} />
                                <button
                                    onClick={handleTranslate}
                                    disabled={prompt.trim().length < 3 || translating || loading || !selectedModel}
                                    style={{
                                        ...btnGhost,
                                        padding: '8px 14px',
                                        borderColor: '#8b5cf6',
                                        color: '#a78bfa',
                                        opacity: (prompt.trim().length < 3 || translating || loading || !selectedModel) ? 0.5 : 1,
                                        cursor: (prompt.trim().length < 3 || translating || loading || !selectedModel) ? 'not-allowed' : 'pointer',
                                    }}
                                    title={`Ctrl+Shift+T — Traduit ton prompt pour ${modelMeta?.label || 'ce modèle'}`}
                                >
                                    {translating ? (
                                        <>
                                            <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                                            Traduction…
                                        </>
                                    ) : (
                                        <>
                                            <Wand2 size={14} />
                                            Traduire pour {modelMeta?.label || 'ce modèle'}
                                        </>
                                    )}
                                </button>
                            </>
                        )}
                    </div>

                    {/* Bulle info post-traduction (conditionnelle) */}
                    {featuresFlags.dialecte && optimizedData && (
                        <div
                            style={{
                                padding: '10px 12px',
                                background: 'rgba(139, 92, 246, 0.08)',
                                border: '1px solid rgba(139, 92, 246, 0.25)',
                                borderRadius: '8px',
                                fontSize: '12px',
                                color: 'var(--text-secondary)',
                                display: 'flex', flexDirection: 'column', gap: '6px',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <Wand2 size={13} style={{ color: '#a78bfa' }} />
                                <span style={{ color: 'var(--text)' }}>
                                    Traduit pour <strong>{modelMeta?.label || 'modèle'}</strong> via <code style={{ fontSize: '11px' }}>{optimizedData.model_used}</code> · {fmtCost(optimizedData.cost_usd)}
                                </span>
                            </div>
                            {optimizedData.rationale && (
                                <>
                                    <button
                                        onClick={() => setRationaleOpen(v => !v)}
                                        style={{
                                            background: 'transparent', border: 'none', padding: 0,
                                            cursor: 'pointer', color: '#a78bfa', fontSize: '12px',
                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                            alignSelf: 'flex-start',
                                        }}
                                    >
                                        {rationaleOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                        {rationaleOpen ? 'Masquer le raisonnement' : 'Voir le raisonnement'}
                                    </button>
                                    {rationaleOpen && (
                                        <div
                                            style={{
                                                padding: '8px 10px', marginTop: '2px',
                                                background: 'var(--input-bg)',
                                                borderLeft: '2px solid #8b5cf6',
                                                borderRadius: '4px',
                                                fontStyle: 'italic', fontSize: '12px',
                                                lineHeight: '1.5', color: 'var(--text-secondary)',
                                                whiteSpace: 'pre-wrap',
                                            }}
                                        >
                                            {optimizedData.rationale}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* S55 P3 : 3 variantes exploration (conditionnel) */}
                    {featuresFlags.dialecte && variations.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                fontSize: '12px', color: 'var(--text-secondary)',
                            }}>
                                <span>
                                    <strong style={{ color: '#a78bfa' }}>{variations.length} variante{variations.length > 1 ? 's' : ''}</strong> — clique sur celle qui te plaît
                                </span>
                                <button
                                    onClick={clearVariations}
                                    style={{
                                        background: 'transparent', border: 'none', padding: '2px 6px',
                                        cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '11px',
                                    }}
                                    title="Effacer les variantes"
                                >
                                    <X size={12} />
                                </button>
                            </div>
                            {variations.map((v, i) => (
                                <div
                                    key={i}
                                    onClick={() => applyVariation(v)}
                                    style={{
                                        padding: '10px 12px',
                                        background: 'rgba(139, 92, 246, 0.04)',
                                        border: '1px solid rgba(139, 92, 246, 0.25)',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                        display: 'flex', flexDirection: 'column', gap: '6px',
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.borderColor = '#8b5cf6';
                                        e.currentTarget.style.background = 'rgba(139, 92, 246, 0.08)';
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.25)';
                                        e.currentTarget.style.background = 'rgba(139, 92, 246, 0.04)';
                                    }}
                                    title="Cliquer pour utiliser cette variante"
                                >
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        fontSize: '11px', color: '#a78bfa', fontWeight: 600,
                                    }}>
                                        <span>Variante {i + 1}</span>
                                        <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>
                                            {fmtCost(v.cost_usd)}
                                        </span>
                                    </div>
                                    <div style={{
                                        fontSize: '12px', color: 'var(--text)',
                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                        lineHeight: '1.45', maxHeight: '80px', overflow: 'auto',
                                        whiteSpace: 'pre-wrap',
                                    }}>
                                        {v.optimized_prompt}
                                    </div>
                                    {v.rationale && (
                                        <div style={{
                                            fontSize: '11px', color: 'var(--text-secondary)',
                                            fontStyle: 'italic', borderLeft: '2px solid rgba(139, 92, 246, 0.4)',
                                            paddingLeft: '8px',
                                        }}>
                                            {v.rationale}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Bouton Revenir au brouillon (conditionnel) */}
                    {featuresFlags.dialecte && rawPromptBackup !== null && rawPromptBackup !== prompt && (
                        <button
                            onClick={handleRevert}
                            disabled={translating || loading}
                            style={{
                                ...btnGhost,
                                padding: '6px 12px', fontSize: '12px',
                                alignSelf: 'flex-start',
                                opacity: (translating || loading) ? 0.5 : 1,
                            }}
                            title="Restaurer ton prompt brut d'origine"
                        >
                            <Undo2 size={13} /> Revenir au brouillon
                        </button>
                    )}
                </div>

                {!loading ? (
                    <button
                        onClick={handleGenerate}
                        disabled={!prompt.trim() || !selectedModel}
                        style={{ ...btnPrimary, opacity: (!prompt.trim() || !selectedModel) ? 0.5 : 1 }}
                        title="Ctrl+Enter"
                    >
                        <Sparkles size={16} /> Générer
                    </button>
                ) : (
                    <button onClick={handleAbort} style={btnDanger}>
                        <X size={16} /> Annuler ({elapsed}s)
                    </button>
                )}
            </div>

            {/* Colonne droite — Résultat + Historique ──────────── */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px', minWidth: 0 }}>

                {/* Zone résultat principale */}
                <div style={{
                    flex: '0 0 auto', minHeight: '380px',
                    background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                    borderRadius: '12px', padding: '20px',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                }}>
                    {loading && (
                        <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                            <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6' }} />
                            <div style={{ marginTop: '14px', fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                                {phaseLabel(phase)}
                            </div>
                            <div style={{ fontSize: '12px', marginTop: '4px' }}>{elapsed}s écoulées</div>
                        </div>
                    )}

                    {!loading && errorResult && (
                        <div style={{
                            width: '100%', padding: '20px',
                            background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239,68,68,0.3)',
                            borderRadius: '10px',
                        }}>
                            <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: '6px' }}>
                                Échec : {errorResult.code || 'unknown'}
                            </div>
                            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '14px' }}>
                                {errorResult.message || '—'}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '14px' }}>
                                {suggestionFor(errorResult.code)}
                            </div>
                            <button onClick={handleRetry} style={btnPrimary}>
                                <RefreshCw size={14} /> Réessayer
                            </button>
                        </div>
                    )}

                    {!loading && !errorResult && result && (
                        <div style={{ width: '100%' }}>
                            <img
                                src={signedResultSrc || ''}
                                alt={result.prompt}
                                                onClick={() => openLightbox(result.primary_asset_url)}
                                style={{
                                    maxWidth: '100%', maxHeight: '520px', borderRadius: '10px',
                                    display: 'block', margin: '0 auto', cursor: 'zoom-in',
                                    border: '1px solid var(--card-border)',
                                }}
                            />
                            <div style={{
                                display: 'flex', gap: '8px', marginTop: '14px',
                                flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center',
                            }}>
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                    {result.model} — {fmtCost(result.cost_usd)}
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button onClick={() => handleDownload(result.primary_asset_url)} style={btnGhost}>
                                        <Download size={14} /> Télécharger
                                    </button>
                                    <button onClick={handleRegenerate} style={btnGhost} title="Régénérer avec un nouveau seed">
                                        <RotateCcw size={14} /> Régénérer
                                    </button>
                                    <button onClick={() => setExportModal({ generationId: result.id })} style={btnGhost}>
                                        <FolderInput size={14} /> Exporter
                                    </button>
                                    <button onClick={() => handleDelete(result.id)} style={{ ...btnGhost, color: '#dc2626' }}>
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {!loading && !result && !errorResult && (
                        <div style={{ textAlign: 'center', opacity: 0.4, color: 'var(--text-secondary)' }}>
                            <ImageIcon size={48} />
                            <div style={{ marginTop: '10px', fontSize: '14px' }}>
                                Sélectionne un modèle, écris ton prompt, puis lance la génération.
                            </div>
                        </div>
                    )}
                </div>

                {/* Historique compact */}
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        marginBottom: '10px',
                    }}>
                        <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text)' }}>
                            Historique ({history.length})
                        </div>
                        <button onClick={refreshHistory} style={btnGhostSmall}>
                            <RefreshCw size={12} />
                        </button>
                    </div>
                    {history.length === 0 ? (
                        <div style={{
                            padding: '30px', textAlign: 'center',
                            color: 'var(--text-secondary)', fontSize: '13px', opacity: 0.6,
                            border: '1px dashed var(--card-border)', borderRadius: '10px',
                        }}>
                            Aucune génération pour le moment.
                        </div>
                    ) : (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                            gap: '10px',
                        }}>
                            {history.map(item => (
                                <HistoryCard key={item.id} item={item}
                                    onClick={() => openLightbox(item.primary_asset_url)}
                                    onDelete={() => handleDelete(item.id)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Export modal ───────────────────────────────────── */}
            {exportModal && (
                <Modal onClose={() => setExportModal(null)} title="Exporter vers un projet">
                    {projects.length === 0 ? (
                        <div style={{ padding: '14px', color: 'var(--text-secondary)' }}>
                            Aucun projet disponible. Crée un projet d'abord.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '400px', overflow: 'auto' }}>
                            {projects.map(p => (
                                <button key={p.id} onClick={() => handleExport(p.id)}
                                    style={{
                                        textAlign: 'left', padding: '10px 14px', borderRadius: '8px',
                                        border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                                        color: 'var(--text)', cursor: 'pointer', fontSize: '13px',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
                                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--card-border)'}
                                >
                                    <div style={{ fontWeight: 600 }}>{p.title || p.name || `Projet ${p.id}`}</div>
                                    {p.description && <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>{p.description}</div>}
                                </button>
                            ))}
                        </div>
                    )}
                </Modal>
            )}

            {/* Onboarding Dialecte (1ʳᵉ utilisation, S54 P1) ────── */}
            {featuresFlags.dialecte && onboarding && (
                <div
                    onClick={handleOnboardDismiss}
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                        zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: 'var(--card-bg)',
                            border: '1px solid rgba(139, 92, 246, 0.4)',
                            borderRadius: '12px', padding: '22px',
                            width: '90%', maxWidth: '440px',
                            boxShadow: '0 10px 40px rgba(139, 92, 246, 0.18)',
                            position: 'relative',
                        }}
                    >
                        <button
                            onClick={handleOnboardDismiss}
                            aria-label="Fermer"
                            style={{
                                position: 'absolute', top: '10px', right: '10px',
                                border: 'none', background: 'transparent', cursor: 'pointer',
                                color: 'var(--text-secondary)', padding: '4px',
                            }}
                        >
                            <X size={18} />
                        </button>
                        <div
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                marginBottom: '12px',
                            }}
                        >
                            <Wand2 size={18} style={{ color: '#a78bfa' }} />
                            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                                Le Dialecte adapte ton prompt à {onboarding.modelLabel}.
                            </div>
                        </div>
                        <div
                            style={{
                                fontSize: '13px', color: 'var(--text-secondary)',
                                lineHeight: '1.55', marginBottom: '18px',
                            }}
                        >
                            Il traduit ton intention dans le style attendu par ce modèle.
                            Tu gardes la main : éditer le résultat, revenir à ton brouillon, régénérer.
                        </div>
                        <button
                            onClick={handleOnboardAccept}
                            style={{
                                ...btnPrimary,
                                width: '100%',
                                background: 'linear-gradient(135deg, #7c3aed, #a78bfa)',
                                boxShadow: '0 4px 14px rgba(139, 92, 246, 0.3)',
                            }}
                        >
                            <Wand2 size={15} /> Traduire maintenant
                        </button>
                    </div>
                </div>
            )}

            {/* Lightbox ───────────────────────────────────────── */}
            {lightbox && (
                <div onClick={() => setLightbox(null)}
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
                        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'zoom-out',
                    }}>
                    <img src={lightbox} alt="preview" style={{ maxWidth: '92%', maxHeight: '92%', borderRadius: '8px' }} />
                </div>
            )}

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes micpulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.5); }
                    50% { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
                }
            `}</style>
        </div>
    );
};

// ─── Sous-composants ────────────────────────────────────────────

const HistoryCard = ({ item, onClick, onDelete }) => {
    const ok = item.status === 'success' && item.primary_asset_url;
    const signedThumb = useSignedSrc(ok ? item.primary_asset_url : null);
    return (
        <div style={{
            position: 'relative', borderRadius: '8px', overflow: 'hidden',
            border: '1px solid var(--card-border)', background: 'var(--input-bg)',
            aspectRatio: '1', cursor: ok ? 'zoom-in' : 'default',
        }} onClick={ok ? onClick : undefined}>
            {ok ? (
                <img src={signedThumb || ''} alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
                <div style={{
                    height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: item.status === 'failed' ? '#dc2626' : 'var(--text-secondary)',
                    fontSize: '11px', padding: '10px', textAlign: 'center',
                }}>
                    {item.status === 'failed' ? `❌ ${item.error_code || 'failed'}` : item.status}
                </div>
            )}
            <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                padding: '6px 8px', background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                fontSize: '10px', color: '#fff', pointerEvents: 'none',
            }}>
                {(item.prompt || '').substring(0, 40)}{item.prompt?.length > 40 ? '…' : ''}
            </div>
            <button onClick={e => { e.stopPropagation(); onDelete(); }}
                style={{
                    position: 'absolute', top: '4px', right: '4px',
                    width: '22px', height: '22px', borderRadius: '50%',
                    border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                <X size={12} />
            </button>
        </div>
    );
};

const Modal = ({ title, onClose, children }) => (
    <div onClick={onClose}
        style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
        <div onClick={e => e.stopPropagation()}
            style={{
                background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                borderRadius: '12px', padding: '20px', width: '90%', maxWidth: '440px',
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div style={{ fontWeight: 700, color: 'var(--text)' }}>{title}</div>
                <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                    <X size={18} />
                </button>
            </div>
            {children}
        </div>
    </div>
);

const Field = ({ label, children }) => (
    <div>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '6px' }}>
            {label}
        </label>
        {children}
    </div>
);

// ─── Styles ─────────────────────────────────────────────────────

const inputBaseStyle = {
    width: '100%', padding: '10px 14px', borderRadius: '10px',
    border: '1px solid var(--card-border)', background: 'var(--input-bg)',
    color: 'var(--text)', fontSize: '14px', outline: 'none',
    boxSizing: 'border-box', transition: 'border-color 0.15s',
};

const selectStyle = { ...inputBaseStyle, cursor: 'pointer', appearance: 'auto' };

const btnBase = {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '10px 18px', borderRadius: '10px', border: 'none',
    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.15s', justifyContent: 'center',
};

const btnPrimary = {
    ...btnBase,
    background: 'linear-gradient(135deg, #1D4ED8, #3b82f6)',
    color: '#fff',
    boxShadow: '0 4px 14px rgba(29, 78, 216, 0.3)',
};

const btnDanger = {
    ...btnBase,
    background: '#dc2626', color: '#fff',
};

const btnGhost = {
    ...btnBase,
    padding: '8px 12px', fontSize: '13px',
    background: 'transparent', color: 'var(--text)',
    border: '1px solid var(--card-border)',
};

const btnGhostSmall = {
    ...btnGhost, padding: '4px 8px', fontSize: '11px',
};

export default StudioImages;
